import {useState} from 'react';
import {useQuery} from '@apollo/client';
import {Alert, Card, Descriptions, Spin, Table, Tag, Typography} from 'antd';
import DBModal from 'components/modal/modal';
import {
  GET_COMPLY_CUBE_CHECKS,
  GET_COMPLY_CUBE_CLIENT_DETAILS,
} from '../resolvers';
import {
  badgeToneForStatus,
  buildChecksInput,
  firstHttpUrl,
  formatBytes,
  formatDate,
  unwrapListResponse,
  COMPLY_CUBE_CHECKS_KEY,
} from '../kycComplyCubeUtils';
import {SignedThumbLink} from './ComplyCubeMedia';
import {
  ComplyCubeTablePagination,
  ComplyCubeUserCell,
} from './ComplyCubeTableShared';
import styles from '../kycDashboard.module.scss';

const {Title, Text} = Typography;

function ClientModalBody({clientId, onNavigateClient}) {
  const [checksPage, setChecksPage] = useState(1);
  const [checksLimit, setChecksLimit] = useState(10);

  const {data, loading, error} = useQuery(GET_COMPLY_CUBE_CLIENT_DETAILS, {
    variables: {input: {clientId}},
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  const {data: checksData, loading: checksLoading} = useQuery(
    GET_COMPLY_CUBE_CHECKS,
    {
      variables: {
        input: buildChecksInput(checksPage, checksLimit, {
          search: '',
          clientId,
          type: '',
          status: '',
          outcome: '',
        }),
      },
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
    },
  );

  const root = data?.getComplyCubeClientDetails;
  const isError = root?.__typename === 'Error';
  const payload =
    root?.__typename === 'ResponseWithComplyCubeClientDetails'
      ? root.data
      : null;
  const client = payload?.client;

  const {rows: checkRows, total: checksTotal} = unwrapListResponse(
    checksData,
    COMPLY_CUBE_CHECKS_KEY,
    'ResponseWithComplyCubeChecks',
  );

  if (loading && !root) {
    return (
      <div className={styles.drawerLoading}>
        <Spin />
      </div>
    );
  }

  if (isError) {
    return (
      <Alert type="error" message={root?.message || 'Failed to load client'} />
    );
  }

  if (error?.message) {
    return <Alert type="error" message={error.message} />;
  }

  return (
    <>
      <Card size="small" className={styles.drawerCard}>
        <Title level={5}>Client</Title>
        {client ? (
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label="Entity name">
              {client.entityName || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Email">
              {client.email || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Type">
              {client.type || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Client ID">
              {client.id || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Name">
              {`${client.personDetails?.firstName || ''} ${
                client.personDetails?.lastName || ''
              }`.trim() || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="DOB">
              {formatDate(client.personDetails?.dob)}
            </Descriptions.Item>
            <Descriptions.Item label="Nationality">
              {client.personDetails?.nationality || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Created">
              {formatDate(client.createdAt)}
            </Descriptions.Item>
            <Descriptions.Item label="Updated">
              {formatDate(client.updatedAt)}
            </Descriptions.Item>
          </Descriptions>
        ) : (
          <Text type="secondary">No client record</Text>
        )}
      </Card>

      <Card size="small" className={styles.drawerCard}>
        <Title level={5}>Checks</Title>
        <Spin spinning={checksLoading}>
          <Table
            size="small"
            rowKey={r => r.id || r._id}
            pagination={false}
            dataSource={checkRows}
            columns={[
              {
                title: 'User',
                key: 'user',
                width: 200,
                render: (_, r) => <ComplyCubeUserCell user={r?.user} />,
              },
              {
                title: 'Entity',
                dataIndex: 'entityName',
                key: 'entityName',
              },
              {
                title: 'Type',
                dataIndex: 'type',
                key: 'type',
              },
              {
                title: 'Client ID',
                dataIndex: 'clientId',
                key: 'clientId',
                render: id =>
                  id && onNavigateClient ? (
                    <button
                      type="button"
                      className={styles.clientIdLink}
                      onClick={() => onNavigateClient(id)}>
                      {id}
                    </button>
                  ) : (
                    id || '-'
                  ),
              },
              {
                title: 'Status',
                dataIndex: 'status',
                key: 'status',
                render: v => (
                  <Tag color={badgeToneForStatus(v)}>{v || '-'}</Tag>
                ),
              },
              {
                title: 'Outcome',
                key: 'outcome',
                render: (_, r) => {
                  const o = r?.result?.outcome;
                  return <Tag color={badgeToneForStatus(o)}>{o || '-'}</Tag>;
                },
              },
              {
                title: 'Created',
                dataIndex: 'createdAt',
                key: 'createdAt',
                render: formatDate,
              },
            ]}
          />
          <ComplyCubeTablePagination
            page={checksPage}
            limit={checksLimit}
            total={checksTotal}
            rowCount={checkRows.length}
            loading={checksLoading}
            onPageChange={(p, l) => {
              setChecksLimit(l);
              setChecksPage(p);
            }}
            itemName="checks"
          />
        </Spin>
      </Card>

      <Card size="small" className={styles.drawerCard}>
        <Title level={5}>Documents</Title>
        {(payload?.documents || []).length ? (
          <div className={styles.docList}>
            {payload.documents.map(doc => (
              <div key={doc.id || doc._id} className={styles.docBlock}>
                {doc.user ? (
                  <div className={styles.docBlockUser}>
                    <ComplyCubeUserCell user={doc.user} />
                  </div>
                ) : null}
                <Text strong>
                  {doc.type || 'Document'}{' '}
                  <Text type="secondary">
                    · {doc.classification || '-'} · {doc.issuingCountry || '-'}
                  </Text>
                </Text>
                <div className={styles.thumbGrid}>
                  {(doc.images || []).map(img => (
                    <SignedThumbLink
                      key={img.id || img.fileName}
                      rawUrl={firstHttpUrl(img.s3, img.downloadLink)}
                      alt={img.fileName || img.documentSide}
                      caption={img.documentSide || img.fileName || 'Image'}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <Text type="secondary">No documents</Text>
        )}
      </Card>

      <Card size="small" className={styles.drawerCard}>
        <Title level={5}>Addresses</Title>
        {(payload?.addresses || []).length ? (
          <ul className={styles.drawerList}>
            {payload.addresses.map(a => (
              <li key={a.id || a._id}>
                {a.user ? (
                  <div className={styles.docBlockUser}>
                    <ComplyCubeUserCell user={a.user} />
                  </div>
                ) : null}
                <Text>
                  {[a.line, a.city, a.state, a.postalCode, a.country]
                    .filter(Boolean)
                    .join(', ') || '-'}
                </Text>
                <div>
                  <Text type="secondary">{formatDate(a.createdAt)}</Text>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <Text type="secondary">No addresses</Text>
        )}
      </Card>

      <Card size="small" className={styles.drawerCard}>
        <Title level={5}>Live photos</Title>
        {(payload?.livePhotos || []).length ? (
          <div className={styles.thumbGrid}>
            {payload.livePhotos.map(photo => (
              <div
                key={photo.id || photo._id}
                className={styles.livePhotoBlock}>
                {photo.user ? (
                  <div className={styles.livePhotoUser}>
                    <ComplyCubeUserCell user={photo.user} />
                  </div>
                ) : null}
                <SignedThumbLink
                  rawUrl={firstHttpUrl(photo.s3, photo.downloadLink)}
                  alt="Live photo"
                  caption={`${formatBytes(photo.size)} · ${
                    photo.contentType || '-'
                  }`}
                />
              </div>
            ))}
          </div>
        ) : (
          <Text type="secondary">No live photos</Text>
        )}
      </Card>
    </>
  );
}

export function ComplyCubeClientModal({
  open,
  clientId,
  onClose,
  onNavigateClient,
}) {
  return (
    <DBModal
      isOpen={open && !!clientId}
      handleClose={onClose}
      width={960}
      destroyOnClose
      content={
        <div className={styles.complyCubeClientModalWrap}>
          <Title level={4} className={styles.complyCubeClientModalTitle}>
            ComplyCube client
          </Title>
          {open && clientId ? (
            <div className={styles.complyCubeClientModalBody}>
              <ClientModalBody
                key={clientId}
                clientId={clientId}
                onNavigateClient={onNavigateClient}
              />
            </div>
          ) : null}
        </div>
      }
    />
  );
}
